import React, { Fragment, useEffect, useState } from 'react';

import { Button, Checkbox, Col, Flex, Form, Input, Row, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import openNotification from '../../components/notification';

import { createPackages, getByIdPackage, getPackage, setIsSuccess, setIsUpdating, updatePackage } from './redux/slice';
import { getMenuForPackageListing } from '../../redux/slice/global';

import BackIcon from '../../components/icon/BackIcon';

const { Text } = Typography;

const menuTranslationKeys = {
    'Dashboard': 'menu_dashboard',
    'User Mgnt.': 'menu_user_mgnt',
    'Client': 'menu_client_mgnt',
    'Packages': 'menu_packages',
    'Stammdaten': 'menu_stammdaten',
    'Warehouse Mgnt.': 'menu_warehouse_mgnt',
    'Time Tracking': 'menu_time_tracking',
    'Construction Diary': 'menu_construction_diary',
    'Vacation Mgnt.': 'menu_vacation_mgnt',
    'Utility': 'menu_utility',
    'Settings': 'menu_settings',
    'Shift Mgnt.': 'menu_shift_mgnt',
    'Vertragsmanager': 'menu_contract_manager',
    'Support-Zeiterfassung': 'menu_support_zeiterfassung',
};

const FIXED_MENUS = ['Dashboard', 'User Management', 'Settings'];

const SortableMenuItem = ({ item, checked, disabled, onChange, t }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.value });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: isDragging ? '#f0f4ff' : 'transparent',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        padding: '8px 12px',
        marginBottom: 2,
        userSelect: 'none',
    };

    return (
        <div ref={setNodeRef} style={style}>
            <span
                {...attributes}
                {...listeners}
                style={{ cursor: 'grab', marginRight: 10, color: '#bbb', fontSize: 16, lineHeight: 1 }}
                title='Ziehen zum Sortieren'
            >
                ⠿
            </span>
            <Text style={{ flex: 1 }}>{t(menuTranslationKeys[item.label] || item.label)}</Text>
            <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={e => onChange(e.target.checked, item.value)}
            />
        </div>
    );
};

const AddUpdatePackages = () => {
    const navigate = useNavigate();

    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const packages = useSelector(state => state.packages);
    const listing = useSelector(state => state.listing);
    const { id } = useParams();

    const [checkedValues, setCheckedValues] = useState([]);
    const [modalType, setModalType] = useState(false);
    const [orderedMenuList, setOrderedMenuList] = useState([]);
    const [savedMenuOrder, setSavedMenuOrder] = useState([]);
    const { t } = useTranslation();

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const applyOrder = (menuList, savedMenu) => {
        if (!menuList.length || !savedMenu.length) return;
        const savedSet = new Set(savedMenu.map(String));
        const inOrder = savedMenu
            .map(mid => menuList.find(m => String(m.value) === String(mid)))
            .filter(Boolean);
        const rest = menuList.filter(m => !savedSet.has(String(m.value)));
        setOrderedMenuList([...inOrder, ...rest]);
    };

    useEffect(() => {
        dispatch(getMenuForPackageListing());
    }, []);

    useEffect(() => {
        if (listing.listing?.menuForPackage?.length > 0) {
            const raw = listing.listing.menuForPackage;
            // If we already have a saved order (edit mode loaded first), apply it now
            if (savedMenuOrder.length > 0) {
                applyOrder(raw, savedMenuOrder);
            } else {
                setOrderedMenuList(raw);
            }
        }
    }, [listing.listing.menuForPackage]);

    useEffect(() => {
        if (!id && orderedMenuList.length > 0) {
            const defaultChecked = orderedMenuList
                .filter(item => FIXED_MENUS.includes(item.label))
                .map(item => item.value);
            setCheckedValues(defaultChecked);
        }
    }, [orderedMenuList]);

    useEffect(() => {
        if (id) {
            setModalType(true);
            dispatch(setIsUpdating(true));
            dispatch(getByIdPackage(id));
        }
    }, [id]);

    useEffect(() => {
        if (Object.keys(packages.package).length > 0) {
            if (!modalType) {
                dispatch(createPackages(packages.package));
            } else {
                if (packages.isUpdating) {
                    form.setFieldsValue({ name: packages?.package?.name });
                    const savedMenu = packages?.package?.menu ?? [];
                    setCheckedValues(savedMenu);
                    setSavedMenuOrder(savedMenu);

                    // Apply order if menuList already loaded; otherwise the menuForPackage effect handles it
                    if (orderedMenuList.length > 0) {
                        applyOrder(orderedMenuList, savedMenu);
                    }
                } else {
                    dispatch(updatePackage({ ...packages?.package, id }));
                }
            }
        }
        if (packages.isSuccess) {
            handleClear();
            navigate('/packages');
            setModalType(false);
            dispatch(setIsSuccess(false));
        }
    }, [packages.isUpdating, packages.package, packages.isSuccess]);

    const handleClear = () => {
        form.resetFields();
        setCheckedValues([]);
        navigate(-1);
    };

    const handleCheckboxChange = (checked, key) => {
        setCheckedValues(prev =>
            checked ? [...prev, key] : prev.filter(item => item !== key)
        );
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setOrderedMenuList(prev => {
                const oldIndex = prev.findIndex(m => m.value === active.id);
                const newIndex = prev.findIndex(m => m.value === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    const onFinish = (values) => {
        const dashboardChecked = checkedValues.some(value =>
            orderedMenuList.find(option => option.value === value)?.label.toLowerCase() === 'dashboard'
        );

        if (!dashboardChecked) {
            openNotification({ message: 'Please select dashboard!', type: 'error' });
            return;
        }

        // Save menu IDs in display (drag) order
        const orderedChecked = orderedMenuList
            .filter(item => checkedValues.some(v => v === item.value || v?.toString() === item.value?.toString()))
            .map(item => item.value);

        if (id) dispatch(setIsUpdating(false));

        dispatch(getPackage({
            name: values.name,
            menu: orderedChecked,
        }));
    };

    return (
        <Fragment>
            <Flex vertical gap={24}>
                <Flex justify='space-between' align='center' gap={6} className='w-100' wrap={true}>
                    <Button
                        type='text'
                        icon={<BackIcon role='button' />}
                        className='bg-white paddingX-3'
                        onClick={() => navigate('/packages')}
                    />
                </Flex>

                <Flex vertical gap={16} className='bg-white padding-2'>
                    <Row>
                        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                            <Form
                                form={form}
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                onFinish={onFinish}
                                autoComplete='off'
                                requiredMark={(label, isRule) => (
                                    <Text>{label}<Text type='danger'>{isRule?.required && '*'}</Text></Text>
                                )}
                            >
                                <Row>
                                    <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                                        <Row>
                                            <Col>
                                                <Form.Item
                                                    label={id ? t('superadmin_packages_editpackage') : t('superadmin_packages_addpackage')}
                                                    name='name'
                                                    rules={[{ required: true, message: t('superadmin_packages_packageerror') }]}
                                                >
                                                    <Input placeholder={t('superadmin_packages_packagesname')} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col xs={24} sm={24} md={24} lg={14} xl={14} xxl={14}>
                                        <Text type='secondary' style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
                                            {t('superadmin_packages_drag_hint') || 'Module per Drag & Drop sortieren'}
                                        </Text>
                                        <div className='bg-gray padding-2 rounded-2'>
                                            <DndContext
                                                sensors={sensors}
                                                collisionDetection={closestCenter}
                                                onDragEnd={handleDragEnd}
                                            >
                                                <SortableContext
                                                    items={orderedMenuList.map(m => m.value)}
                                                    strategy={verticalListSortingStrategy}
                                                >
                                                    {orderedMenuList.map(item => (
                                                        <SortableMenuItem
                                                            key={item.value}
                                                            item={item}
                                                            checked={checkedValues.some(v => v === item.value || v?.toString() === item.value?.toString())}
                                                            disabled={FIXED_MENUS.includes(item.label)}
                                                            onChange={handleCheckboxChange}
                                                            t={t}
                                                        />
                                                    ))}
                                                </SortableContext>
                                            </DndContext>
                                        </div>
                                    </Col>
                                </Row>

                                <Flex justify='flex-end' gap={10} wrap={true} className='paddingY-2'>
                                    <Button danger type='primary' htmlType='button' onClick={handleClear}>
                                        {t('superadmin_common_cancelButton')}
                                    </Button>
                                    <Button type='primary' htmlType='submit' loading={packages?.loading}>
                                        {t('superadmin_common_submitButton')}
                                    </Button>
                                </Flex>
                            </Form>
                        </Col>
                    </Row>
                </Flex>
            </Flex>
        </Fragment>
    );
};

export default AddUpdatePackages;
